import { type Actors as A, type Features, FeaturesSchema, featureKeys } from '@mntn/auth-types'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useLocalStorage } from 'usehooks-ts'

const FEATURE_TOGGLES_STORAGE_KEY = 'select-ui:feature-toggles'
const disabledFeatures = FeaturesSchema.parse({})

type Feature = keyof Features
type FeatureToggles = Partial<Record<Feature, boolean>>

const resolveFeatureToggles = (features: Features, toggles: FeatureToggles) => {
  const resolvedFeatures = { ...features }

  for (const feature of featureKeys) {
    resolvedFeatures[feature] = {
      ...features[feature],
      enabled: features[feature].enabled && (toggles[feature] ?? features[feature].defaultValue),
    }
  }

  return resolvedFeatures
}

const FeaturesContext = createContext<
  | {
      actorFeatures: Features
      effectivePrincipal: ({ tenant: object } & A.Kind<'user'>) | undefined
      hasFeatureSettings: boolean
      resolvedFeatures: Features
      setFeature: (feature: Feature, enabled: boolean) => void
    }
  | undefined
>(undefined)

export function FeaturesProvider({
  children,
  principal,
}: {
  children: ReactNode
  principal?: { tenant: object } & A.Kind<'user'>
}) {
  const storageKey = principal
    ? `${FEATURE_TOGGLES_STORAGE_KEY}:${principal.actor.userId}`
    : FEATURE_TOGGLES_STORAGE_KEY
  const [toggles, setToggles] = useLocalStorage<FeatureToggles>(storageKey, {})
  const actorFeatures = principal?.actor.features ?? disabledFeatures

  useEffect(() => {
    setToggles(currentToggles => {
      const nextToggles = { ...currentToggles }
      let hasChanged = false

      for (const feature of featureKeys) {
        if (!actorFeatures[feature].enabled && nextToggles[feature] !== undefined) {
          delete nextToggles[feature]
          hasChanged = true
        }
      }

      return hasChanged ? nextToggles : currentToggles
    })
  }, [actorFeatures, setToggles])

  const resolvedFeatures = useMemo(() => resolveFeatureToggles(actorFeatures, toggles), [actorFeatures, toggles])

  const effectivePrincipal = useMemo(
    () =>
      principal
        ? {
            ...principal,
            actor: {
              ...principal.actor,
              features: resolvedFeatures,
            },
          }
        : undefined,
    [principal, resolvedFeatures],
  )

  const setFeature = useCallback(
    (feature: Feature, enabled: boolean) => {
      if (!principal) return

      setToggles(currentToggles => {
        const nextToggles = { ...currentToggles }
        const actorFeature = actorFeatures[feature]

        if (!actorFeature.enabled || enabled === actorFeature.defaultValue) {
          delete nextToggles[feature]
          return nextToggles
        }

        nextToggles[feature] = enabled
        return nextToggles
      })
    },
    [actorFeatures, principal, setToggles],
  )

  const value = useMemo(
    () => ({
      actorFeatures,
      effectivePrincipal,
      hasFeatureSettings: featureKeys.some(feature => actorFeatures[feature].enabled),
      resolvedFeatures,
      setFeature,
    }),
    [actorFeatures, effectivePrincipal, resolvedFeatures, setFeature],
  )

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>
}

export function useFeatures() {
  const context = useContext(FeaturesContext)

  if (!context) {
    throw new Error('useFeatures must be used within FeaturesProvider')
  }

  return context
}
